import { useEffect, useContext, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { differenceInMinutes } from "date-fns";
import { toast } from "react-hot-toast";

import ToastDisplay from "../components/ToastDisplay";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Pagination from "../components/Pagination";
import PageLoader from "../components/PageLoader";
import Empty from "../components/Empty";

const Devices = () => {
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [devices, setDevices] = useState([]);
	const [meta, setMeta] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams({});
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [filtermodal, setFilterModal] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [deviceModal, setDeviceModal] = useState(false);
	const [errors, setErrors] = useState([]);

	const [name, setName] = useState("");
	const [type, setType] = useState("");
	const [plate, setPlate] = useState("");

	const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
	const [searchType, setSearchType] = useState(searchParams.get("type") || "");

	const searchFields = [
		{ key: "q", value: searchQ },
		{ key: "type", value: searchType },
	];

	const filterFields = [
		{
			key: "type",
			value: searchType,
			defaultVal: "",
			edit: setSearchType,
		},
	];

	const requiredFields = [
		{ key: "name", value: name },
		{ key: "type", value: type },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: { "X-Auth-Token": session },
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/devices`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setDevices(data.devices);
			setMeta(data.meta);
		}
		setLoading(false);
	};

	const search = (e) => {
		e.preventDefault();
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "") {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
		setFilterModal(false);
	};

	const removeFilter = (e, func, key) => {
		e.preventDefault();
		func("");
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "" && field.key !== key) {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
	};

	const clearFilters = (e) => {
		e.preventDefault();
		setSearchParams({});
		filterFields.map((field) => {
			field.edit(field.defaultVal);
		});
	};

	const closeDeviceModal = () => {
		setDeviceModal(false);
		setName("");
		setType("");
		setPlate("");
		setErrors([]);
	};

	const isEmpty = (value) => {
		return value === "" || value === null || value === undefined;
	};

	const saveDevice = async () => {
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				name: name,
				type: type,
				plate: plate,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/devices/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			closeDeviceModal();
			navigate(`/devices/${data.device_id}`);
		}
		setActionLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		saveDevice();
	};

	const isOnline = (dateTime) => {
		return differenceInMinutes(new Date(), new Date(dateTime)) < 3;
	};

	useEffect(() => {
		document.title = "Devices | Pisara Dashboard";
		if (profile) {
			if (!profile.role.devices_view) {
				navigate("/");
			} else {
				getData();
				if (filterFields.every((field) => isEmpty(field.value))) {
					setShowFilters(false);
				} else {
					setShowFilters(true);
				}
			}
		}
	}, [searchParams, profile]);

	return (
		<>
			<Container className="gx-5">
				<Row className="content-header flex-row-left mb-20">
					<Col>
						<h1 className="text-xl m-0">Devices</h1>
					</Col>
				</Row>
				<Row className="flex-row-left mb-20">
					<Col className="col-5">
						<Form onSubmit={search}>
							<div className="search-container bg-white flex-row-left">
								<i className="ri-search-line search-icon" />
								<Form.Control
									className="text-sm"
									placeholder="Search personnel"
									value={searchQ}
									onChange={(e) => setSearchQ(e.target.value)}
								/>
								<Button
									variant="light"
									className="filter-btn flex-column"
									onClick={() => setFilterModal(true)}
								>
									<i className="ri-equalizer-3-fill m-0" />
								</Button>
							</div>
						</Form>
					</Col>
					<Col className="col-7 flex-row-right">
						<Pagination
							meta={meta}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
							searchFields={searchFields}
							className="pull-right flex-row-left"
						/>
						<Button
							variant="light"
							className={`refresh-btn flex-column ${
								!profile?.role?.devices_write && "no-margin"
							}`}
							onClick={getData}
						>
							<i className="ri-restart-line opacity-7" />
						</Button>
						{profile?.role?.devices_write && (
							<Button
								className="control-btn btn-sm btn-default text-medium-gotham text-sm"
								onClick={() => setDeviceModal(true)}
							>
								<i className="ri-computer-line" />
								Add Device
							</Button>
						)}
					</Col>
				</Row>
				<Row className="content-table-header bg-white gx-0">
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Name</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Type</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Status
						</span>
					</Col>
				</Row>
				{loading ? (
					<PageLoader className="bg-white p-50" />
				) : devices?.length === 0 ? (
					<Empty label="No devices to show." className="bg-white" />
				) : (
					devices.map((device) => (
						<Link key={device.id} to={`/devices/${device.id}`}>
							<Row className="content-table-item flex-row-left bg-white gx-0">
								<Col className="p-10 ellipsis">
									<span className="text-sm">{device.name}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{device.device_type}</span>
								</Col>
								<Col className="p-10 ellipsis">
									{device.last_heartbeat && isOnline(device.last_heartbeat) ? (
										<span className="device-status label-md text-medium-gotham text-white fit online">
											Online
										</span>
									) : (
										<span className="device-status label-md text-medium-gotham text-white fit offline">
											Offline
										</span>
									)}
								</Col>
							</Row>
						</Link>
					))
				)}
			</Container>
			<Modal
				show={deviceModal}
				className="admin-content"
				centered
				onHide={closeDeviceModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">Add Device</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Device Information
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Name</Form.Label>
								<Form.Control
									autoFocus
									className={`text-sm form-input ${
										errors.includes("name") && "border-danger"
									}`}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</Col>
						</Row>
						<Row className="mb-50">
							<Col>
								<Form.Label className="text-sm">Type</Form.Label>
								<Form.Select
									className={`text-sm form-input ${
										errors.includes("type") && "border-danger"
									}`}
									value={type}
									onChange={(e) => setType(e.target.value)}
								>
									<option value=""></option>
									<option value="Stationary">
										Stationary (For campus entrance/exits.)
									</option>
									<option value="Vehicle">Vehicle (For school buses.)</option>
								</Form.Select>
							</Col>
						</Row>
						{type === "Vehicle" && (
							<Row className="mb-50">
								<Col>
									<Form.Label className="text-sm">License Plate</Form.Label>
									<Form.Control
										autoFocus
										className={`text-sm form-input ${
											errors.includes("plate") && "border-danger"
										}`}
										value={plate}
										onChange={(e) => setPlate(e.target.value)}
									/>
								</Col>
							</Row>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm text-medium-inter me-2"
							onClick={closeDeviceModal}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							className="btn-default btn-sm action-btn flex-column text-sm text-medium-inter"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Save"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</>
	);
};

export default Devices;
